import * as React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { ComparablePlanType } from '@coverright/data-access/types/medicare';
import { useDeviceWidth } from '@coverright/utils';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { PlanCart } from './plan-selection-types';
import { CheckoutStep1 } from './ui/checkout-step1';
import { CheckoutStep2 } from './ui/checkout-step2';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CheckoutModalProps {
  open: boolean;
  cart: PlanCart;
  step: 1 | 2;
  onClose: () => void;
  onNextStep: () => void;
  onRemovePlan: (type: ComparablePlanType) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CheckoutModal = ({
  open, cart, step, onClose, onNextStep, onRemovePlan,
}: CheckoutModalProps) => {
  const phone = useDeviceWidth('sm');

  const items       = [cart.ma, cart.mg, cart.pdp].filter((i): i is NonNullable<typeof i> => i !== null);
  const total       = items.reduce((sum, i) => sum + i.premium, 0);
  const primaryType = cart.ma?.type ?? cart.mg?.type ?? cart.pdp?.type ?? null;

  const title    = step === 1 ? 'Review your selection' : 'Final Step';
  const maxWidth = step === 2 ? 750 : 600;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{zIndex: 1500}}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: '90%',
          maxWidth,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'max-width 300ms ease',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: '20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#101828' }}>{title}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#6b7280' }}>
          <X size={24} />
        </IconButton>
      </Box>

      {/* Scrollable content */}
      <OverlayScrollbarsComponent
        style={{ flex: 1, minHeight: 0, maxHeight: phone ? undefined : 'calc(100vh - 120px)' }}
        options={{ paddingAbsolute: true, scrollbars: { visibility: 'hidden' } }}
        defer
      >
        <Box sx={{ p: 3 }}>
          {step === 1 && (
            <CheckoutStep1
              items={items}
              total={total}
              primaryType={primaryType}
              cart={cart}
              onNextStep={onNextStep}
              onRemovePlan={onRemovePlan}
              onBrowse={onClose}
            />
          )}
          {step === 2 && <CheckoutStep2 onClose={onClose} />}
        </Box>
      </OverlayScrollbarsComponent>
    </Dialog>
  );
};
